import { useMemo, useState } from 'react';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import { useAppSelector } from 'state/hooks';
import { selectUserProducts } from 'state/userSlice';

import {
  PRODUCT_DETAILS,
  ProductInfo,
  ProductType,
} from 'features/app_header/productSwitcherConstants';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { classNames } from 'features/common/utils';

import styles from './ProductSwitcher.module.css';

const matchesPath = (currentHref: string, targetHref: string) => {
  const current = new URL(currentHref);
  const target = new URL(targetHref, currentHref);
  return (
    current.origin === target.origin &&
    current.pathname.startsWith(target.pathname)
  );
};

export function ProductSwitcher() {
  const products = useAppSelector(selectUserProducts);
  const [switcherOpen, setSwitcherOpen] = useState<boolean>(false);
  const [showProductsList, setShowProductsList] = useState<boolean>(true);
  const currentHref = window.location.href;

  if (!products) {
    return null;
  }
  const {
    productsToShow,
    customDashboards,
    seenOrgsUniq,
    seenPlanningOrgsUniq,
  } = useMemo(() => {
    // Deduplicate products, prioritizing unexpired subscriptions
    // Except custom_dashboard products
    const realizedProducts: Partial<Record<ProductType, ProductInfo>> = {};
    const dashboards: ProductInfo[] = [];
    const seenPlanningOrgs: string[] = [];
    const seenOrgs: string[] = [];

    for (const product of products) {
      const pType = product.product_type;

      if (!product.subscription_expired && pType === ProductType.insight_full) {
        seenPlanningOrgs.push(product.org_id);
      }
      seenOrgs.push(product.org_id);

      if (pType === ProductType.custom_dashboard) {
        dashboards.push(product);
        continue;
      }

      const realizedProduct = realizedProducts[pType];
      if (!realizedProduct || realizedProduct.subscription_expired) {
        realizedProducts[pType] = product;
      }
    }

    // If the user has any custom dashboards, we add one tile for it
    if (dashboards.length > 0) {
      realizedProducts[ProductType.custom_dashboard] = dashboards[0];
    }

    return {
      productsToShow: [...Object.values(realizedProducts)],
      customDashboards: dashboards,
      seenOrgsUniq: [...new Set(seenOrgs)],
      seenPlanningOrgsUniq: [...new Set(seenPlanningOrgs)],
    };
  }, [products]);

  const get_patched_url = (product: ProductInfo) => {
    // This is a short term fix to make up for insight shortcomings: INST-39853
    let url = product.product_url;
    if (
      product.product_type === ProductType.insight_full &&
      seenOrgsUniq.length > 0
    ) {
      if (seenPlanningOrgsUniq.length === 1) {
        // But if there's only one planning org, we can switch directly to it
        url += `?org_id=${product.org_id}`;
      } else {
        // If there are multiple orgs in play, patch the URL so going back to planning gets you the org chooser
        url = url.replace('/planning-solutions', '');
        url += '/chooseProductOrg';
      }
    }
    return url;
  };

  if (productsToShow.length <= 1) {
    return null;
  }

  const closeSwitcher = () => {
    setSwitcherOpen(false);
    setShowProductsList(true);
  };

  return (
    <div className={styles.platform_switcher_base}>
      <IconButton
        onClick={() => setSwitcherOpen(true)}
        className={styles.platform_switcher_button}
      >
        <FontAwesomeIcon name="grid" size="21px" />
      </IconButton>
      {switcherOpen && (
        <>
          <div // eslint-disable-line jsx-a11y/no-static-element-interactions
            className={styles.click_catcher}
            onClick={closeSwitcher}
            onKeyUp={(ev) => {
              if (ev.key === 'Escape') {
                closeSwitcher();
              }
            }}
          />
          <div className={styles.platform_switcher_body}>
            {showProductsList ? (
              <div className={styles.switch_wrapper}>
                {productsToShow.map((product) => {
                  const isCustomDashboards =
                    product.product_type === ProductType.custom_dashboard;
                  const isExpired = product.subscription_expired;
                  const patchedUrl = get_patched_url(product);
                  const isSelected = isCustomDashboards
                    ? customDashboards.some((subscription) =>
                        matchesPath(currentHref, get_patched_url(subscription)),
                      )
                    : matchesPath(currentHref, patchedUrl);

                  const handleClick = () => {
                    if (isCustomDashboards) {
                      setShowProductsList(false);
                      return;
                    }
                    window.location.href = patchedUrl;
                  };

                  return (
                    <Button
                      key={product.product_url}
                      className={classNames(
                        styles.product_tile,
                        isExpired && styles.expired,
                        isSelected && styles.selected,
                        isCustomDashboards && styles.dashboard,
                      )}
                      disabled={isExpired}
                      onClick={handleClick}
                    >
                      <span
                        className={classNames(
                          styles.platform_type_icon,
                          isSelected && styles.selected_icon,
                        )}
                      >
                        {PRODUCT_DETAILS[product.product_type]?.image}
                      </span>
                      <span className={styles.platform_type_label}>
                        {isCustomDashboards
                          ? 'Custom Dashboards'
                          : product.product_name}
                      </span>
                    </Button>
                  );
                })}
              </div>
            ) : (
              <div className={styles.custom_dashboards_wrapper}>
                <IconButton
                  className={styles.back_btn}
                  size="small"
                  onClick={() => setShowProductsList(true)}
                >
                  <FontAwesomeIcon name="chevron-left" />
                </IconButton>

                <span className={styles.section_title}>Custom Dashboards</span>
                <div className={styles.custom_dashboards_list}>
                  {customDashboards.map((subscription) => {
                    const patchedUrl = get_patched_url(subscription);
                    const isSelected = matchesPath(currentHref, patchedUrl);
                    const isExpired = subscription.subscription_expired;

                    return (
                      <Button
                        key={subscription.product_url}
                        className={styles.custom_dashboard_item}
                        disabled={isExpired}
                        onClick={() => {
                          window.location.href = patchedUrl;
                        }}
                      >
                        <span
                          title={subscription.product_name}
                          className={isSelected && styles.selected_in_list}
                        >
                          {subscription.product_name}
                        </span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
